import React from 'react';
import { CheckCircle, AlertTriangle, Clock, AlertCircle } from 'lucide-react';

type StatusType =
  | 'approved'
  | 'rejected'
  | 'under_review'
  | 'pending'
  | 'submitted'
  | 'active'
  | 'expired'
  | 'declined'
  | 'cancelled'
  | string;

interface StatusBadgeProps {
  status: StatusType;
  size?: 'sm' | 'md' | 'lg';
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status, size = 'md' }) => {
  const getStatusConfig = (raw: StatusType) => {
    const status = String(raw || '').trim().toLowerCase().replace(/\s+/g, '_');
    switch (status) {
      case 'approved':
        return {
          icon: <CheckCircle className="tw-w-4 tw-h-4" />,
          text: 'Approved',
          bgColor: 'tw-bg-green-50',
          textColor: 'tw-text-green-700',
          borderColor: 'tw-border-green-200'
        };
      case 'rejected':
        return {
          icon: <AlertTriangle className="tw-w-4 tw-h-4" />,
          text: 'Rejected',
          bgColor: 'tw-bg-red-50',
          textColor: 'tw-text-red-700',
          borderColor: 'tw-border-red-200'
        };
      case 'declined':
        return {
          icon: <AlertTriangle className="tw-w-4 tw-h-4" />,
          text: 'Declined',
          bgColor: 'tw-bg-red-50',
          textColor: 'tw-text-red-700',
          borderColor: 'tw-border-red-200'
        };
      case 'under_review':
        return {
          icon: <Clock className="tw-w-4 tw-h-4" />,
          text: 'Under Review',
          bgColor: 'tw-bg-yellow-50',
          textColor: 'tw-text-yellow-700',
          borderColor: 'tw-border-yellow-200'
        };
      case 'pending':
        return {
          icon: <Clock className="tw-w-4 tw-h-4" />,
          text: 'Pending',
          bgColor: 'tw-bg-blue-50',
          textColor: 'tw-text-blue-700',
          borderColor: 'tw-border-blue-200'
        };
      case 'submitted':
        return {
          icon: <Clock className="tw-w-4 tw-h-4" />,
          text: 'Submitted',
          bgColor: 'tw-bg-blue-50',
          textColor: 'tw-text-blue-700',
          borderColor: 'tw-border-blue-200'
        };
      case 'cancelled':
        return {
          icon: <AlertCircle className="tw-w-4 tw-h-4" />,
          text: 'Cancelled',
          bgColor: 'tw-bg-gray-50',
          textColor: 'tw-text-gray-700',
          borderColor: 'tw-border-gray-200'
        };
      case 'active':
        return {
          icon: <CheckCircle className="tw-w-4 tw-h-4" />,
          text: 'Approved',
          bgColor: 'tw-bg-[#95D1B1]',
          textColor: 'tw-text-[#000000]',
          borderColor: 'tw-border-green-200'
        };
      case 'expired':
        return {
          icon: <AlertCircle className="tw-w-4 tw-h-4" />,
          text: 'Expired',
          bgColor: 'tw-bg-gray-50',
          textColor: 'tw-text-gray-700',
          borderColor: 'tw-border-gray-200'
        };
      default:
        return {
          icon: <AlertCircle className="tw-w-4 tw-h-4" />,
          text: 'Unknown',
          bgColor: 'tw-bg-gray-50',
          textColor: 'tw-text-gray-700',
          borderColor: 'tw-border-gray-200'
        };
    }
  };

  const config = getStatusConfig(status);
  
  // Size classes
  const sizeClasses = {
    sm: 'tw-text-md tw-px-7 tw-py-1',
    md: 'tw-text-md tw-px-2.5 tw-py-1',
    lg: 'tw-text-md tw-px-3 tw-py-1.5'
  };

  return (
    <span 
      className={`tw-flex tw-border tw-rounded-[6px] ${config.bgColor} ${config.textColor} ${config.borderColor} ${sizeClasses[size]}`}
    >
      <span className="tw-mr-1.5 tw-p-1">{config.icon}</span>
      {config.text}
    </span>
  );
};

export default StatusBadge;